import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { ChangeEvent, memo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DeleteBin6LineIcon,
  EditLineIcon,
  Information2LineIcon,
  RotationLock,
  SortAscIcon,
  SortDescIcon,
} from '~/assets/icons';
import { IAccount } from '~/utils/interface/account';
import styles from '../Technician.module.scss';
const cx = classNames.bind(styles);

type Props = {
  pageSize: number;
  data: IAccount[];
  form: any;
  technicianIds?: Array<string>;
  setRefreshData: (refresh: boolean) => void;
  onDetail?: (row: IAccount) => void;
  onEdit?: (row: IAccount) => void;
  onReset?: (row: IAccount) => void;
  onDelete?: (row: IAccount) => void;
  handleCheck?: (row: IAccount, checked: boolean) => void;
  handleCheckAll?: (event: ChangeEvent<HTMLInputElement>, checked: boolean) => void;
};

const TechnicianTable = (props: Props) => {
  const { t } = useTranslation('admin/technician');

  const handleSelectedOptions = (field: string, value: any) => {
    props.form.setFieldValue(field, value);
  };

  const handleSort = (field: string, type: string) => {
    props.form.setFieldValue('order_by', { field, type });
  };

  const getRatingStatus = (status: number) => {
    if (status === 1) {
      return <Box className='text-[#ED1002]'>{t('ratingStatus.improvement')}</Box>;
    }
    if (status === 2) {
      return <Box className='text-[#3D9149]'>{t('ratingStatus.meetRequirement')}</Box>;
    }
    if (status === 3) {
      return <Box className='text-[#3D9149]'>{t('ratingStatus.excellence')}</Box>;
    }
    return '';
  };

  return (
    <TableContainer
      sx={{
        border: '1px solid #D9D9D9',
        borderRadius: '1px',
        maxHeight: '100vh',
      }}
    >
      <Table stickyHeader>
        <TableHead className='bg-[#005F73]'>
          <TableRow>
            <TableCell padding='checkbox'>
              <Checkbox
                onChange={props.handleCheckAll}
                sx={{
                  color: 'white',
                  '&.Mui-checked': {
                    color: 'white',
                  },
                }}
              />
            </TableCell>
            <TableCell className={cx('min-w-[70px]', 'table-header')} align='center'>
              {t('table.no')}
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.username')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'username' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('username', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('username', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.username}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('username', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.name')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'name' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('name', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('name', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.name}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('name', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[150px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.phone')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'phone' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('phone', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('phone', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.phone}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('phone', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[250px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.email')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'email' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('email', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('email', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.email}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('email', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[250px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.address')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'address' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('address', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('address', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.address}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('address', e.target.value)}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[200px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.totalUsersAssigned')}</Box>
                {props.form.values.order_by.type === 'asc' &&
                props.form.values.order_by.field === 'total_users_assigned' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_users_assigned', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_users_assigned', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_users_assigned}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => {
                  if (e.target.value) {
                    handleSelectedOptions('total_users_assigned', e.target.value ?? 0);
                  } else {
                    handleSelectedOptions('total_users_assigned', 0);
                  }
                }}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[140px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.totalSessionKtv')}</Box>
                {props.form.values.order_by.type === 'asc' &&
                props.form.values.order_by.field === 'total_session_ktv' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('total_session_ktv', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('total_session_ktv', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.total_session_ktv}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => {
                  if (e.target.value) {
                    handleSelectedOptions('total_session_ktv', e.target.value ?? 0);
                  } else {
                    handleSelectedOptions('total_session_ktv', 0);
                  }
                }}
              />
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.ratingStatus')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'rating_status' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('rating_status', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('rating_status', 'asc');
                    }}
                  />
                )}
              </Box>
            </TableCell>

            <TableCell className='text-white border-l border-white min-w-[130px]'>
              <Box className={cx('header-title-container')}>
                <Box className={cx('header-title')}>{t('table.createdAt')}</Box>
                {props.form.values.order_by.type === 'asc' && props.form.values.order_by.field === 'created_at' ? (
                  <SortDescIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px]'
                    onClick={() => {
                      handleSort('created_at', 'desc');
                    }}
                  />
                ) : (
                  <SortAscIcon
                    fill='white'
                    fontSize='small'
                    className='w-[20px] h-[17px] cursor-pointer'
                    onClick={() => {
                      handleSort('created_at', 'asc');
                    }}
                  />
                )}
              </Box>
              <TextField
                defaultValue={props.form.values.created_at}
                placeholder={t('longSearch')}
                className='header-search-input'
                onBlur={(e) => handleSelectedOptions('created_at', e.target.value)}
              />
            </TableCell>

            <TableCell className={cx('w-[150px]', 'table-header')} align='center'>
              {t('action')}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {props.data.map((row, index) => (
            <TableRow key={index}>
              <TableCell padding='checkbox'>
                <Checkbox
                  checked={
                    (props.technicianIds ?? []).find((technicianId) => technicianId === row.username) ? true : false
                  }
                  onChange={(event, checked) => props.handleCheck && props.handleCheck(row, checked)}
                  sx={{
                    color: '#005F73',
                    '&.Mui-checked': {
                      color: '#005F73',
                    },
                  }}
                />
              </TableCell>
              <TableCell align='center'>{index + 1 + (props.form.values.page_index - 1) * props.pageSize}</TableCell>
              <TableCell align='center'>
                <Box className='w-[150px]'>{row.username}</Box>
              </TableCell>
              <TableCell align='center'>
                <Box className='w-[150px]'>{row.name}</Box>
              </TableCell>
              <TableCell align='center'>{row.phone}</TableCell>
              <TableCell align='center'>
                <Box className='w-[250px]'>{row.email}</Box>
              </TableCell>
              <TableCell align='center'>
                <Box className='w-[250px]'>{row.address}</Box>
              </TableCell>
              <TableCell align='center'>{row.total_users_assigned}</TableCell>
              <TableCell align='center'>{row.total_session_ktv}</TableCell>
              <TableCell align='center'>{getRatingStatus(Number(row.rating_status))}</TableCell>
              <TableCell align='center'>{row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}</TableCell>
              <TableCell align='center'>
                <Box className='flex'>
                  <IconButton onClick={() => props?.onDetail && props.onDetail(row)}>
                    <Information2LineIcon fill='#72777B' />
                  </IconButton>
                  <IconButton onClick={() => props?.onEdit && props.onEdit(row)}>
                    <EditLineIcon />
                  </IconButton>
                  <IconButton>
                    <RotationLock fill='#ED1002' onClick={() => props.onReset && props.onReset(row)} />
                  </IconButton>
                  {row.total_users_assigned ? (
                    <IconButton>
                      <DeleteBin6LineIcon fill='#72777B' />
                    </IconButton>
                  ) : (
                    <IconButton onClick={() => props?.onDelete && props.onDelete(row)}>
                      <DeleteBin6LineIcon fill='#ED1002' />
                    </IconButton>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default memo(TechnicianTable);
